import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Box,
  Button,
  Typography,
  IconButton,
  Grid,
  TextField,
  Divider,
  Paper,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import VoiceWaveform from "../../../doctor-console/Utils/VoiceWaveform";
import { StyledButton } from "../../../doctor-console/StyledComponents";
import { CountdownCircleTimer } from "react-countdown-circle-timer";

// ─── Structure-driven helpers (shared with CarePlanActivityModal schema) ───────
// The voice review form is rendered from the care_plan_structures template so it
// always matches whatever fields the backend/AI returns. These pure helpers build
// default values, read/write nested paths, and parse/format date-time values.

const labelize = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getDefaultFieldValue = (field) => {
  if (!field) return "";
  if (field.type === "multi-select") return [];
  if (field.type === "table") return [];
  if (field.type === "boolean") return false;
  if (field.type === "number") return "";
  if (field.type === "object" || field.type === "group") {
    return buildInitialValues(field.structure || []);
  }
  return "";
};

function buildInitialValues(fields = []) {
  return fields.reduce((acc, field) => {
    if (!field?.field) return acc;
    acc[field.field] = getDefaultFieldValue(field);
    return acc;
  }, {});
}

// Merge template defaults with the AI/voice returned data so every structure field
// renders (empty if not extracted) while keeping any extracted values.
const deepMergeDefaults = (defaults, data) => {
  if (Array.isArray(defaults)) {
    return Array.isArray(data) ? data : defaults;
  }
  if (defaults && typeof defaults === "object") {
    const out = { ...defaults };
    if (data && typeof data === "object" && !Array.isArray(data)) {
      Object.keys(data).forEach((key) => {
        out[key] = deepMergeDefaults(defaults[key], data[key]);
      });
    }
    return out;
  }
  return data !== undefined ? data : defaults;
};

const getNestedValue = (source, path) =>
  path.reduce(
    (current, key) =>
      current && typeof current === "object" ? current[key] : undefined,
    source
  );

const updateNestedValue = (source, path, value) => {
  const cloned = Array.isArray(source) ? [...source] : { ...(source || {}) };
  let cursor = cloned;
  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    const nextValue =
      cursor[key] && typeof cursor[key] === "object"
        ? Array.isArray(cursor[key])
          ? [...cursor[key]]
          : { ...cursor[key] }
        : {};
    cursor[key] = nextValue;
    cursor = nextValue;
  }
  cursor[path[path.length - 1]] = value;
  return cloned;
};

const pad2 = (n) => String(n).padStart(2, "0");

const parseDateValue = (raw) => {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateForApi = (date, includeTime) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  if (includeTime) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
      date.getDate()
    )}T${pad2(date.getHours())}:${pad2(date.getMinutes())}:00`;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const formatTimeForApi = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const parseTimeFieldValue = (raw) => {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  const hm = s.match(/^(\d{1,2}):(\d{2})/);
  if (hm) {
    const d = new Date(2000, 0, 1, Number(hm[1]), Number(hm[2]), 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(`2000-01-01T${s}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normalizeKey = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getStructureId = (structure) =>
  Number(
    structure?.care_plan_id || structure?.id || structure?.template_id || 0
  ) || 0;

// ─── Voice → structure data normalization ─────────────────────────────────────
// The AI voice response does not always key test data as the "test_items" table
// the new template expects (it may return tests[]/test/testName/orderDetail/etc).
// These helpers reshape the returned data so the spoken tests actually populate
// the Test table rows (the same way medication.drug_name already populates).

// Field names that may hold a test's name across voice response variants.
const TEST_NAME_FIELDS = [
  "test_name",
  "testName",
  "name",
  "title",
  "orderDetail",
  "order_detail",
  "order",
  "investigation",
  "investigation_name",
  "investigationName",
  "procedure",
  "procedure_name",
  "test",
  "label",
  "display",
  "value",
];

// Container keys whose values (arrays/objects/strings) hold test entries.
const TEST_CONTAINER_KEYS = [
  "testitems",
  "tests",
  "orders",
  "investigations",
  "labtests",
  "diagnostics",
  "orderitems",
  "items",
  "procedures",
  "testlist",
];

const SPLIT_REGEX = /,| and |&|\/|\n|;/gi;

// Keywords that indicate a radiology/imaging test. Anything else defaults to
// laboratory (doctor can still change the per-row Category dropdown).
const RADIOLOGY_KEYWORDS = [
  "x-ray",
  "x ray",
  "xray",
  "radiograph",
  "ct ",
  "ct-",
  "ct scan",
  "cect",
  "hrct",
  "mri",
  "ultrasound",
  "usg",
  "sonograph",
  "doppler",
  "mammogram",
  "mammography",
  "angiogram",
  "angiography",
  "fluoroscopy",
  "pet scan",
  "pet-ct",
  "dexa",
  "bone scan",
  "barium",
  "ivp",
  "hsg",
  "kub",
  "scan",
  "imaging",
];

const detectTestCategory = (name = "") => {
  const text = String(name || "").toLowerCase();
  if (!text) return "laboratory";
  if (RADIOLOGY_KEYWORDS.some((kw) => text.includes(kw))) return "radiology";
  return "laboratory";
};

const pickTestName = (obj) => {
  for (const field of TEST_NAME_FIELDS) {
    const raw = obj?.[field];
    if (raw != null && String(raw).trim()) return String(raw).trim();
  }
  // Fallback: first non-empty string property.
  for (const value of Object.values(obj || {})) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const toTestItem = (src) => {
  if (src && typeof src === "object") {
    const name = pickTestName(src);
    return {
      test_name: name,
      category: normalizeCategoryValue(src.category) || detectTestCategory(name),
      test_description:
        src.test_description || src.testDescription || src.description || "",
      emergency: Boolean(src.emergency || src.isEmergency || src.stat),
      instruction: src.instruction || src.instructions || "",
    };
  }
  const name = String(src || "").trim();
  return {
    test_name: name,
    category: detectTestCategory(name),
    test_description: "",
    emergency: false,
    instruction: "",
  };
};

const isTestContainerKey = (key) => {
  const nk = normalizeKey(key);
  return (
    TEST_CONTAINER_KEYS.includes(nk) ||
    nk.includes("test") ||
    nk.includes("investigation") ||
    nk.includes("procedure")
  );
};

// Deep-scan the voice data for test entries regardless of how they are nested.
// Only invoked for test-type plans, so grabbing names within test containers is safe.
const deepExtractTestItems = (data) => {
  const items = [];
  const visit = (node, keyHint) => {
    if (node == null) return;
    if (Array.isArray(node)) {
      node.forEach((child) => visit(child, keyHint));
      return;
    }
    if (typeof node === "object") {
      const hasExplicitName =
        (node.test_name && String(node.test_name).trim()) ||
        (node.testName && String(node.testName).trim());
      if (hasExplicitName) {
        items.push(toTestItem(node));
      } else if (isTestContainerKey(keyHint)) {
        const name = pickTestName(node);
        if (name) items.push(toTestItem({ ...node, test_name: name }));
      }
      Object.entries(node).forEach(([k, v]) => visit(v, k));
      return;
    }
    if (typeof node === "string" && node.trim() && isTestContainerKey(keyHint)) {
      node
        .split(SPLIT_REGEX)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((name) => items.push(toTestItem({ test_name: name })));
    }
  };
  visit(data, "");
  return items;
};

// Build the test_items[] rows from whatever shape the voice response used.
const buildTestItemsFromVoiceData = (data = {}) => {
  let items = [];

  if (Array.isArray(data.test_items) && data.test_items.length > 0) {
    items = data.test_items.map(toTestItem);
  } else if (Array.isArray(data.tests) && data.tests.length > 0) {
    items = data.tests.map(toTestItem);
  } else if (Array.isArray(data.orders) && data.orders.length > 0) {
    items = data.orders.map(toTestItem);
  } else if (data.test && typeof data.test === "object") {
    items = [toTestItem(data.test)];
  } else if (data.test_name || data.testName || data.name) {
    items = [
      toTestItem({
        test_name: data.test_name || data.testName || data.name,
        test_description: data.test_description || data.description,
        instruction: data.instruction || data.instructions,
        emergency: data.emergency,
      }),
    ];
  } else {
    const orderText = String(
      data.orderDetail || data.order_detail || data.description || ""
    ).trim();
    if (orderText) {
      items = orderText
        .split(SPLIT_REGEX)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((name) => toTestItem({ test_name: name }));
    }
  }

  // Last resort: deep-scan the entire payload for anything test-shaped.
  if (items.filter((item) => item.test_name).length === 0) {
    items = deepExtractTestItems(data);
  }

  // Keep only named rows and drop duplicates.
  const seen = new Set();
  return items.filter((item) => {
    const key = normalizeKey(item.test_name);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeCategoryValue = (rawCategory) => {
  const cat = String(
    Array.isArray(rawCategory) ? rawCategory[0] : rawCategory || ""
  ).toLowerCase();
  if (!cat) return "";
  if (cat.includes("radio")) return "radiology";
  if (cat.includes("lab")) return "laboratory";
  if (["laboratory", "radiology", "other"].includes(cat)) return cat;
  return "other";
};

// Reshape the raw voice data into the field names the matched structure expects.
const normalizeVoiceDataForStructure = (structure, planType, rawData) => {
  const data = rawData && typeof rawData === "object" ? { ...rawData } : {};
  const nameKey = normalizeKey(structure?.name);
  const isTest = planType === "test" || nameKey === "test";

  if (isTest) {
    const items = buildTestItemsFromVoiceData(data);
    if (items.length > 0) data.test_items = items;
    // Plan-level category radio is hidden (per-row category is used instead), but
    // keep a valid value for the API — prefer any returned value, else derive it.
    const normalizedCategory = normalizeCategoryValue(data.category);
    data.category =
      normalizedCategory || items[0]?.category || "laboratory";
    if (!data.remark && (data.notes || data.clinicalIndication)) {
      data.remark = data.notes || data.clinicalIndication;
    }
  }

  return data;
};

const isEmptyish = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

// Whether a structure field actually holds captured data (used to hide the empty
// fields/tables of a plan so the review shows only what was spoken/added).
const fieldHasData = (field, value) => {
  if (!field) return false;
  switch (field.type) {
    case "table":
      return (
        Array.isArray(value) &&
        value.some(
          (row) =>
            row &&
            typeof row === "object" &&
            Object.values(row).some((cell) => !isEmptyish(cell) && cell !== false)
        )
      );
    case "multi-select":
      return Array.isArray(value) && value.length > 0;
    case "boolean":
      return value === true;
    case "object":
    case "group":
      return (field.structure || []).some((nested) =>
        fieldHasData(nested, value?.[nested.field])
      );
    default:
      return !isEmptyish(value);
  }
};

// Align a table row's keys to the column field names (case/format-insensitive).
const alignRowToColumns = (columns = [], row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return row;
  const colByKey = new Map();
  columns.forEach((col) => {
    if (col?.field) colByKey.set(normalizeKey(col.field), col.field);
  });
  const out = {};
  Object.entries(row).forEach(([rawKey, value]) => {
    const canonicalKey = colByKey.get(normalizeKey(rawKey)) || rawKey;
    if (out[canonicalKey] === undefined || isEmptyish(out[canonicalKey])) {
      out[canonicalKey] = value;
    }
  });
  return out;
};

// Recursively remap voice-data keys to the structure's canonical field names so
// AI-returned data populates the correct fields even when key formatting differs
// (e.g. historyOfPresentIllness ↔ history_of_present_illness). Unmatched keys are
// preserved so nothing is silently dropped.
const alignDataToStructure = (structureFields, data) => {
  if (
    !Array.isArray(structureFields) ||
    !data ||
    typeof data !== "object" ||
    Array.isArray(data)
  ) {
    return data;
  }

  const fieldByKey = new Map();
  structureFields.forEach((field) => {
    if (field?.field) fieldByKey.set(normalizeKey(field.field), field);
  });

  const aligned = {};
  Object.entries(data).forEach(([rawKey, value]) => {
    const field = fieldByKey.get(normalizeKey(rawKey));
    const canonicalKey = field ? field.field : rawKey;

    if (field) {
      if (
        (field.type === "object" || field.type === "group") &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        aligned[canonicalKey] = alignDataToStructure(field.structure || [], value);
        return;
      }
      if (field.type === "table" && Array.isArray(value)) {
        aligned[canonicalKey] = value.map((row) =>
          alignRowToColumns(field.columns || [], row)
        );
        return;
      }
    }

    if (aligned[canonicalKey] === undefined || isEmptyish(aligned[canonicalKey])) {
      aligned[canonicalKey] = value;
    }
  });

  return aligned;
};

const VoiceAssistantModal = ({
  open,
  onClose,
  onSubmit,
  patientId,
  carePlanStructures = [],
}) => {
  const [language, setLanguage] = useState("en");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [timerKey, setTimerKey] = useState(0);
  const [limitMessage, setLimitMessage] = useState("");
  const [step, setStep] = useState("record");
  const [reviewPlans, setReviewPlans] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const limitTimeoutRef = useRef(null);
  const elapsedRef = useRef(0);
  const recordingStartRef = useRef(null);

  const languages = [
    { code: "en", name: "English" },
    { code: "hi", name: "Hindi" },
    { code: "bn", name: "Bengali" },
    { code: "as", name: "Assamese" },
    { code: "gu", name: "Gujarati" },
    { code: "kn", name: "Kannada" },
    { code: "ml", name: "Malayalam" },
    { code: "pa", name: "Punjabi" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "od", name: "Odia" },
    { code: "mix", name: "Mixed Language" },
  ];

  // Format remaining seconds as MM:SS
  const formatMMSS = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      handleStopRecording();
      setAudioBlob(null);
      setLimitMessage("");
      setLanguage("en");
      setIsPaused(false);
      setStep("record");
      setReviewPlans([]);
      setIsSubmitting(false);
    }
  }, [open]);
  const toLabel = (value = "") =>
    String(value)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const normalizeType = (rawType = "") => {
    const key = String(rawType || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    const aliasMap = {
      medication: "medication",
      medicine: "medication",
      medicines: "medication",
      test: "test",
      tests: "test",
      lab: "test",
      labs: "test",
      note: "clinical-notes",
      notes: "clinical-notes",
      clinicalnotes: "clinical-notes",
      clinicalnote: "clinical-notes",
      monitoring: "monitoring",
      vitals: "monitoring",
      nutrition: "diet",
      diet: "diet",
      appointment: "appointment",
      appointments: "appointment",
      task: "task-list",
      tasks: "task-list",
      tasklist: "task-list",
    };
    return aliasMap[key] || "";
  };

  const getValueByPath = (source, path = []) =>
    path.reduce(
      (current, key) => (current && typeof current === "object" ? current[key] : undefined),
      source
    );

  const inferCarePlanType = (plan) => {
    const normalizedExisting = normalizeType(plan?.type);
    if (normalizedExisting) return normalizedExisting;
    const normalizedName = normalizeType(
      plan?.name || plan?.activity_name || plan?.activityType || ""
    );
    if (normalizedName) return normalizedName;

    const existingType = String(plan?.type || "").trim().toLowerCase();
    if (existingType && existingType !== "activity") return existingType;
    const data = plan?.data || {};
    if (Array.isArray(data?.medications) || data?.medication) return "medication";
    if (Array.isArray(data?.tests) || data?.test) return "test";
    if (data?.testName || data?.test_name || data?.clinicalIndication || data?.clinical_indication) {
      return "test";
    }
    const categoryValue = Array.isArray(data?.category)
      ? data.category.map((v) => String(v).toLowerCase()).join(" ")
      : String(data?.category || "").toLowerCase();
    if (categoryValue.includes("lab") || categoryValue.includes("test")) return "test";
    if (data?.orderDetail || data?.order_detail) return "test";
    if (Array.isArray(data?.monitoringItems)) return "monitoring";
    if (Array.isArray(data?.tasks)) return "task-list";
    if (data?.dietType || data?.feedingMode) return "diet";
    if (data?.start_datetime || data?.recurrence_enabled) return "appointment";
    const dataKeys = Object.keys(data || {}).map((k) => String(k).toLowerCase());
    if (dataKeys.some((k) => k.includes("test"))) return "test";
    return "activity";
  };

  // Maps the voice review "type" (from inferCarePlanType) to a care_plan_structures
  // template name so we can look up the correct field schema.
  const TYPE_TO_STRUCTURE_NAME = {
    medication: "medication",
    test: "test",
    "clinical-notes": "clinical notes",
    monitoring: "note",
    diet: "nutrition",
    appointment: "appointment",
    "task-list": "task",
  };

  // Resolve the care_plan_structures definition for a given voice plan.
  // Priority: exact care_plan_id/template_id match → name/type match → mapped name → partial.
  const findStructureForPlan = useCallback(
    (plan) => {
      if (!Array.isArray(carePlanStructures) || carePlanStructures.length === 0)
        return null;

      const planStructureId = Number(plan?.care_plan_id || 0);
      if (planStructureId) {
        const byId = carePlanStructures.find(
          (s) => getStructureId(s) === planStructureId
        );
        if (byId) return byId;
      }

      const planKey = normalizeKey(plan?.type || plan?.name);
      if (planKey) {
        const byName = carePlanStructures.find(
          (s) =>
            normalizeKey(s?.name) === planKey ||
            normalizeKey(s?.type) === planKey
        );
        if (byName) return byName;

        const mappedName = TYPE_TO_STRUCTURE_NAME[plan?.type];
        if (mappedName) {
          const mappedKey = normalizeKey(mappedName);
          const byMapped = carePlanStructures.find(
            (s) => normalizeKey(s?.name) === mappedKey
          );
          if (byMapped) return byMapped;
        }

        const partial = carePlanStructures.find((s) => {
          const nameKey = normalizeKey(s?.name);
          return (
            nameKey &&
            (nameKey.includes(planKey) || planKey.includes(nameKey))
          );
        });
        if (partial) return partial;
      }

      return null;
    },
    [carePlanStructures]
  );

  // Write a value at a nested path within a review plan's data object.
  const setPlanFieldValue = (planIndex, path, value) => {
    setReviewPlans((prev) =>
      prev.map((plan, idx) => {
        if (idx !== planIndex) return plan;
        const nextData = updateNestedValue(plan.data || {}, path, value);
        return { ...plan, data: nextData };
      })
    );
  };

  const deleteReviewPlan = (planIndex) => {
    setReviewPlans((prev) => prev.filter((_, idx) => idx !== planIndex));
  };

  // Append a fresh blank entry for a group (e.g. add another Medication), using
  // the group's structure so it renders the same editable form as the AI entries.
  const addBlankEntryForSection = (section) => {
    const structure = section?.structure;
    const referenceType =
      section?.entries?.[0]?.plan?.type ||
      (structure
        ? inferCarePlanType({ type: structure.name, name: structure.name })
        : "activity");
    const newPlan = {
      type: referenceType,
      name: structure?.name || section?.title || "",
      care_plan_id: structure ? getStructureId(structure) || undefined : undefined,
      data: structure ? buildInitialValues(structure.structure || []) : {},
    };
    setReviewPlans((prev) => [...prev, newPlan]);
  };

  // Human-friendly title for a single review entry (accordion header).
  const getPlanEntryLabel = (plan, structure, index) => {
    const data = plan?.data || {};
    const planType = inferCarePlanType(plan);
    const baseName = structure?.name || labelize(planType);

    // Tests list every order inside the table, so the entry header stays generic
    // (e.g. "Test 1") instead of repeating the first test name.
    if (planType === "test" || normalizeKey(structure?.name) === "test") {
      return `${baseName} ${index + 1}`;
    }

    const med = data?.medication;
    if (med && typeof med === "object" && (med.drug_name || med.name)) {
      return med.drug_name || med.name;
    }
    if (data?.drug_name) return data.drug_name;
    if (data?.doctor || data?.specialty) {
      return [data.doctor, data.specialty].filter(Boolean).join(" • ");
    }
    return `${baseName} ${index + 1}`;
  };

  // ── Structure-driven field renderer (mirrors CarePlanActivityModal.renderField) ──
  const renderStructureField = (
    planIndex,
    field,
    sourceData,
    pathPrefix = [],
    hideEmpty = false
  ) => {
    if (!field?.field) return null;
    const path = [...pathPrefix, field.field];
    const key = path.join(".");
    const fieldValue = getNestedValue(sourceData, path);
    const label = field.label || labelize(field.field);
    const isRequired = field.required === true;
    const onFieldChange = (targetPath, value) =>
      setPlanFieldValue(planIndex, targetPath, value);

    // In "captured-only" mode (e.g. Clinical Notes), skip fields with no data so
    // the review shows only what the doctor actually dictated.
    if (hideEmpty && !fieldHasData(field, fieldValue)) return null;

    if (field.type === "object") {
      return (
        <Grid item xs={12} key={key}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {label}
              {isRequired ? " *" : ""}
            </Typography>
            <Grid container spacing={1.5}>
              {(field.structure || []).map((nestedField) =>
                renderStructureField(planIndex, nestedField, sourceData, path, hideEmpty)
              )}
            </Grid>
          </Paper>
        </Grid>
      );
    }

    if (field.type === "group") {
      // Only render a group when its order_type/condition matches the current data.
      if (field.condition) {
        const condValue = getNestedValue(sourceData, [field.condition.field]);
        if (condValue !== field.condition.value) return null;
      }
      return (
        <Grid item xs={12} key={key}>
          <Paper variant="outlined" sx={{ p: 2, mt: 0.5, borderRadius: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, mb: 1.5, color: "#121959", fontSize: "0.88rem" }}
            >
              {field.label || label}
            </Typography>
            <Grid container spacing={1.5}>
              {(field.structure || []).map((nestedField) =>
                renderStructureField(planIndex, nestedField, sourceData, path, hideEmpty)
              )}
            </Grid>
          </Paper>
        </Grid>
      );
    }

    if (field.type === "boolean") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(fieldValue)}
                onChange={(e) => onFieldChange(path, e.target.checked)}
              />
            }
            label={label}
          />
        </Grid>
      );
    }

    if (field.type === "select") {
      const options = Array.isArray(field.options) ? field.options : [];
      // Reference selects (e.g. specialty/doctor) have no static options — fall
      // back to an editable text field so the extracted value is preserved.
      if (options.length === 0) {
        return (
          <Grid item xs={12} sm={6} key={key}>
            <TextField
              fullWidth
              size="small"
              label={label}
              required={isRequired}
              value={fieldValue ?? ""}
              onChange={(e) => onFieldChange(path, e.target.value)}
            />
          </Grid>
        );
      }
      return (
        <Grid item xs={12} sm={6} key={key}>
          <FormControl fullWidth size="small" required={isRequired}>
            <InputLabel required={isRequired}>{label}</InputLabel>
            <Select
              label={label}
              value={fieldValue ?? ""}
              onChange={(e) => onFieldChange(path, e.target.value)}
            >
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {labelize(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "multi-select") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <FormControl fullWidth size="small" required={isRequired}>
            <InputLabel required={isRequired}>{label}</InputLabel>
            <Select
              multiple
              label={label}
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={(e) => onFieldChange(path, e.target.value)}
              renderValue={(selected) =>
                (selected || []).map((item) => labelize(item)).join(", ")
              }
            >
              {(field.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {labelize(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "radio") {
      // Tests now carry a per-row Category column, so the plan-level Category
      // radio is redundant — hide it (its data value is still kept for the API).
      if (normalizeKey(field.field) === "category" && pathPrefix.length === 0) {
        return null;
      }
      return (
        <Grid item xs={12} key={key}>
          <FormControl required={isRequired}>
            <FormLabel sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#374151", mb: 0.5 }}>
              {label}
              {isRequired ? " *" : ""}
            </FormLabel>
            <RadioGroup
              row
              value={fieldValue ?? ""}
              onChange={(e) => onFieldChange(path, e.target.value)}
            >
              {(field.options || []).map((opt) => (
                <FormControlLabel
                  key={opt}
                  value={opt}
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: "0.83rem" }}>{labelize(opt)}</Typography>}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === "datetime") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.3 }}>
            {label}
            {isRequired ? " *" : ""}
          </Typography>
          <DatePicker
            selected={parseDateValue(fieldValue)}
            onChange={(date) =>
              onFieldChange(path, date ? formatDateForApi(date, true) : "")
            }
            showTimeSelect
            timeIntervals={15}
            dateFormat="dd-MM-yyyy h:mm aa"
            timeCaption="Time"
            placeholderText="dd-mm-yyyy --:--"
            customInput={<TextField size="small" fullWidth />}
            popperProps={{ strategy: "fixed" }}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "date") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.3 }}>
            {label}
            {isRequired ? " *" : ""}
          </Typography>
          <DatePicker
            selected={parseDateValue(fieldValue)}
            onChange={(date) =>
              onFieldChange(path, date ? formatDateForApi(date, false) : "")
            }
            dateFormat="dd-MM-yyyy"
            placeholderText="dd-mm-yyyy"
            customInput={<TextField size="small" fullWidth />}
            popperProps={{ strategy: "fixed" }}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "time") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.3 }}>
            {label}
            {isRequired ? " *" : ""}
          </Typography>
          <DatePicker
            selected={parseTimeFieldValue(fieldValue)}
            onChange={(date) => onFieldChange(path, date ? formatTimeForApi(date) : "")}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            customInput={<TextField size="small" fullWidth />}
            popperProps={{ strategy: "fixed" }}
            isClearable
          />
        </Grid>
      );
    }

    if (field.type === "table") {
      const rows = Array.isArray(fieldValue) ? fieldValue : [];
      const baseColumns = field.columns || [];
      // For the tests table, add a per-row Category column (auto-detected from the
      // test name, but editable) so lab vs radiology is marked on each test.
      const isTestItemsTable =
        field.field === "test_items" ||
        baseColumns.some((col) => col.field === "test_name");
      const hasCategoryColumn = baseColumns.some(
        (col) => normalizeKey(col.field) === "category"
      );
      let columns = baseColumns;
      if (isTestItemsTable && !hasCategoryColumn) {
        const categoryColumn = {
          field: "category",
          label: "Category",
          type: "select",
          options: ["laboratory", "radiology", "other"],
        };
        const testNameIdx = baseColumns.findIndex(
          (col) => col.field === "test_name"
        );
        columns =
          testNameIdx >= 0
            ? [
                ...baseColumns.slice(0, testNameIdx + 1),
                categoryColumn,
                ...baseColumns.slice(testNameIdx + 1),
              ]
            : [categoryColumn, ...baseColumns];
      }
      const emptyRow = () =>
        columns.reduce(
          (acc, col) => ({ ...acc, [col.field]: col.type === "boolean" ? false : "" }),
          {}
        );
      const addRow = () => onFieldChange(path, [...rows, emptyRow()]);
      const removeRow = (rowIdx) =>
        onFieldChange(path, rows.filter((_, i) => i !== rowIdx));
      const changeCell = (rowIdx, colField, value) =>
        onFieldChange(
          path,
          rows.map((row, i) => (i === rowIdx ? { ...row, [colField]: value } : row))
        );

      return (
        <Grid item xs={12} key={key}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#121959" }}>
              {label}
              {isRequired ? " *" : ""}
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: "none" }}>
              Add Row
            </Button>
          </Box>
          {rows.length > 0 ? (
            <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5fffe" }}>
                    {columns.map((col) => (
                      <TableCell key={col.field} sx={{ fontWeight: 700, fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                        {col.label || labelize(col.field)}
                        {col.required ? " *" : ""}
                      </TableCell>
                    ))}
                    <TableCell sx={{ width: 36 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, rowIdx) => (
                    <TableRow key={rowIdx}>
                      {columns.map((col) => {
                        if (col.type === "boolean") {
                          return (
                            <TableCell key={col.field} sx={{ py: 0.4, px: 1, textAlign: "center" }}>
                              <Checkbox
                                size="small"
                                checked={Boolean(row[col.field])}
                                onChange={(e) => changeCell(rowIdx, col.field, e.target.checked)}
                                sx={{ p: 0 }}
                              />
                            </TableCell>
                          );
                        }
                        if (col.type === "select") {
                          return (
                            <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 160 }}>
                              <FormControl fullWidth size="small">
                                <Select
                                  displayEmpty
                                  value={row[col.field] ?? ""}
                                  onChange={(e) => changeCell(rowIdx, col.field, e.target.value)}
                                  sx={{ fontSize: "0.78rem" }}
                                >
                                  <MenuItem value="" disabled sx={{ fontSize: "0.78rem" }}>
                                    <em>Select…</em>
                                  </MenuItem>
                                  {(col.options || []).map((opt) => (
                                    <MenuItem key={opt} value={opt} sx={{ fontSize: "0.78rem" }}>
                                      {labelize(opt)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={col.field} sx={{ py: 0.4, px: 1, minWidth: 140 }}>
                            <TextField
                              size="small"
                              fullWidth
                              type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
                              value={row[col.field] ?? ""}
                              onChange={(e) => changeCell(rowIdx, col.field, e.target.value)}
                              InputLabelProps={col.type === "date" ? { shrink: true } : undefined}
                              sx={{ "& .MuiInputBase-input": { fontSize: "0.78rem", py: "5px" } }}
                            />
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ py: 0.4, px: 0.5 }}>
                        <IconButton size="small" onClick={() => removeRow(rowIdx)} sx={{ color: "#ef4444" }}>
                          <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Box
              onClick={addRow}
              sx={{
                border: "1px dashed #d1d5db",
                borderRadius: 1,
                p: 1.5,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#21cdc0", backgroundColor: "#f5fffe" },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Click "Add Row" to add {String(label).toLowerCase()} entries
              </Typography>
            </Box>
          )}
        </Grid>
      );
    }

    const isMultiline = field.type === "textarea";
    const inputType = field.type === "number" ? "number" : "text";
    return (
      <Grid item xs={12} sm={isMultiline ? 12 : 6} key={key}>
        <TextField
          fullWidth
          size="small"
          label={label}
          type={inputType}
          value={fieldValue ?? ""}
          multiline={isMultiline}
          rows={isMultiline ? 3 : undefined}
          required={isRequired}
          onChange={(e) => onFieldChange(path, e.target.value)}
        />
      </Grid>
    );
  };

  const prevRecordingRef = useRef(isRecording);

  // Detect transitions of isRecording to reset/start/stop timer
  useEffect(() => {
    // start -> increment key to ensure fresh timer instance
    if (isRecording && !prevRecordingRef.current) {
      setTimerKey((k) => k + 1);
    }
    // stop -> increment key to reset (and the timer will be hidden because isRecording=false)
    if (!isRecording && prevRecordingRef.current) {
      setTimerKey((k) => k + 1);
    }
    prevRecordingRef.current = isRecording;
  }, [isRecording]);

  const handleStartRecording = async () => {
    try {
      if (!window.isSecureContext) {
        setLimitMessage(
          "Microphone requires a secure connection. Please use HTTPS or localhost.",
        );
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setLimitMessage(
          "Microphone access is not supported in this browser/environment.",
        );
        return;
      }

      if (typeof window.MediaRecorder === "undefined") {
        setLimitMessage(
          "Audio recording is not supported in this browser/environment.",
        );
        return;
      }

      clearTimeout(limitTimeoutRef.current);
      elapsedRef.current = 0;
      recordingStartRef.current = Date.now();
      setLimitMessage("");

      try {
        if (
          navigator.permissions &&
          typeof navigator.permissions.query === "function"
        ) {
          const permissionStatus = await navigator.permissions.query({
            name: "microphone",
          });

          if (permissionStatus.state === "denied") {
            setLimitMessage(
              "Microphone permission is blocked. Please allow microphone access in browser site settings.",
            );
            return;
          }
        }
      } catch (permissionErr) {
        console.warn(
          "Could not query microphone permission state:",
          permissionErr,
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        clearTimeout(limitTimeoutRef.current);
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setIsPaused(false);

      // Auto-stop after 4 minutes (240 seconds)
      limitTimeoutRef.current = setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
          mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
        }
        setIsRecording(false);
        setIsPaused(false);
        setLimitMessage("Recording limit reached (4 minutes).");
      }, 240000);
    } catch (err) {
      console.error("Error starting recording:", err);
      if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
        setLimitMessage(
          "Microphone permission denied. Please allow access in browser settings and try again.",
        );
      } else if (err?.name === "NotFoundError") {
        setLimitMessage(
          "No microphone device found. Please connect a microphone and retry.",
        );
      } else if (err?.name === "NotReadableError") {
        setLimitMessage(
          "Microphone is currently in use by another app/tab. Please close it and retry.",
        );
      } else if (err?.name === "AbortError") {
        setLimitMessage(
          "Microphone initialization was interrupted. Please try again.",
        );
      } else if (err?.name === "OverconstrainedError") {
        setLimitMessage(
          "Requested audio settings are not supported by this microphone.",
        );
      } else {
        setLimitMessage(
          "Failed to access microphone. Please check browser permissions and connection security.",
        );
      }
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
      }
      setIsRecording(false);
      setIsPaused(false);
      clearTimeout(limitTimeoutRef.current);
    }
  };

  const handlePauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      if (mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.pause();
        const now = Date.now();
        elapsedRef.current += now - recordingStartRef.current;
        setIsPaused(true);
      }
    }
  };

  const handleResumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      if (mediaRecorderRef.current.state === "paused") {
        mediaRecorderRef.current.resume();
        recordingStartRef.current = Date.now();
        setIsPaused(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      setLimitMessage("No recording available. Please record audio first.");
      return;
    }

    if (onSubmit) {
      setIsSubmitting(true);
      setLimitMessage("");
      try {
        const result = await onSubmit({
          mode: "transcribe",
          audioBlob,
          language,
        });
        console.log("result", result);
        const plans = Array.isArray(result?.carePlans)
          ? result.carePlans
          : Array.isArray(result?.care_plans)
            ? result.care_plans
            : [];
        console.log("result?.carePlans", plans);
        if (plans.length === 0) {
          setLimitMessage("No care plans detected from voice. Please retry.");
          return;
        }
        setReviewPlans(
          plans.map((plan) => {
            const type = inferCarePlanType(plan);
            const structure = findStructureForPlan({ ...plan, type });
            const baseData =
              plan?.data && typeof plan.data === "object" ? plan.data : {};
            // Reshape voice data into the structure's field names (e.g. spoken
            // tests → test_items table rows) so nothing is dropped on review.
            const normalizedRaw = normalizeVoiceDataForStructure(
              structure,
              type,
              baseData
            );
            // Align returned keys to the template's canonical field names
            // (camelCase ↔ snake_case ↔ spaced), incl. nested objects & table rows.
            const alignedRaw = structure
              ? alignDataToStructure(structure.structure || [], normalizedRaw)
              : normalizedRaw;
            // Merge template defaults so every structure field renders (empty if
            // not extracted) while preserving the values the AI returned.
            const data = structure
              ? deepMergeDefaults(
                  buildInitialValues(structure.structure || []),
                  alignedRaw
                )
              : alignedRaw;
            // Diagnostic: if a Test plan produced no rows, log the raw shape so the
            // exact field the AI used can be mapped in buildTestItemsFromVoiceData.
            if (
              type === "test" &&
              (!Array.isArray(data?.test_items) || data.test_items.length === 0)
            ) {
              try {
                console.warn(
                  "[VoiceReview] Test plan produced no test_items. Raw plan JSON:\n" +
                    JSON.stringify(plan, null, 2)
                );
              } catch (_) {
                console.warn(
                  "[VoiceReview] Test plan produced no test_items. Raw data:",
                  baseData
                );
              }
            }
            return {
              ...plan,
              type,
              care_plan_id:
                Number(plan?.care_plan_id || 0) ||
                (structure ? getStructureId(structure) || undefined : undefined),
              data,
            };
          })
        );
        setStep("review");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const resolveActualEditPath = (plan, path = []) => {
    if (!Array.isArray(path) || path.length === 0) return path;
    const planType = inferCarePlanType(plan);

    if (planType === "medication") {
      const medicationKeyMap = {
        name: "name",
        frequency: "frequency",
        start: "start",
        end: "end",
        period: "period",
        periodUnit: "periodUnit",
        safety: "safety",
      };

      if (path[0] === "medications" && typeof path[1] === "number") {
        if (path[2] === "medication") {
          const nestedField = path[3];
          const mappedField = medicationKeyMap[nestedField] || nestedField;
          const selectedMedication = getValueByPath(plan?.data || {}, ["medications", path[1]]) || {};
          // Support both shapes:
          // 1) medications[i].frequency (flat)
          // 2) medications[i].medication.frequency (nested)
          if (selectedMedication?.medication && typeof selectedMedication.medication === "object") {
            return ["medications", path[1], "medication", mappedField];
          }
          return ["medications", path[1], mappedField];
        }
        if (
          ["priority", "intent", "category", "reason", "course_of_therapy", "notes"].includes(
            path[2]
          )
        ) {
          return [path[2]];
        }
      }

      // Single-medication shape: data.medication (not data.medications[]).
      // Legacy fallback mapping used only when no care_plan_structure matches a plan.
      if (path[0] === "medication") {
        if (path[1] === "medication") {
          const nestedField = path[2];
          const mappedField = medicationKeyMap[nestedField] || nestedField;
          const medicationRoot = getValueByPath(plan?.data || {}, ["medication"]) || {};
          // Support both shapes:
          // 1) data.medication.frequency (flat)
          // 2) data.medication.medication.frequency (nested)
          if (medicationRoot?.medication && typeof medicationRoot.medication === "object") {
            return ["medication", "medication", mappedField];
          }
          return ["medication", mappedField];
        }
        if (
          ["priority", "intent", "category", "reason", "course_of_therapy", "notes"].includes(
            path[1]
          )
        ) {
          return [path[1]];
        }
      }
    }

    if (planType === "test") {
      if (path[0] === "__test_name__") {
        // Synthetic item path used only for display grouping.
        // Persist edits on real top-level test payload.
        return path.length >= 3 ? [path[2]] : [];
      }

      if (path[0] === "tests" && typeof path[1] === "number") {
        if (["testName", "priority", "clinicalIndication", "frequency", "scheduledDateTime"].includes(path[2])) {
          return ["tests", path[1], path[2]];
        }
      }
    }

    return path;
  };

  const handleEditPlanDataByPath = (planIndex, path, value) => {
    setReviewPlans((prev) =>
      prev.map((plan, idx) => {
        if (idx !== planIndex) return plan;
        const actualPath = resolveActualEditPath(plan, path);
        if (!Array.isArray(actualPath) || actualPath.length === 0) return plan;
        const nextData = Array.isArray(plan.data) ? [...plan.data] : { ...(plan.data || {}) };
        let cursor = nextData;
        for (let i = 0; i < actualPath.length - 1; i += 1) {
          const key = actualPath[i];
          const existing = cursor[key];
          cursor[key] = Array.isArray(existing) ? [...existing] : { ...(existing || {}) };
          cursor = cursor[key];
        }
        cursor[actualPath[actualPath.length - 1]] = value;
        return { ...plan, data: nextData };
      })
    );
  };

  const renderEditableField = (planIndex, fieldKey, fieldValue, path = []) => {
    const keyPath = [...path, fieldKey];
    const fieldLabel = String(fieldKey || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

    if (Array.isArray(fieldValue)) {
      return (
        <Grid item xs={12} key={keyPath.join(".")}>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {fieldLabel}
            </Typography>
            <Box sx={{ pl: 1, mt: 0.5, display: "flex", flexDirection: "column", gap: 1 }}>
              {fieldValue.map((item, idx) => {
                if (item && typeof item === "object") {
                  return (
                    <Paper key={`${keyPath.join(".")}.${idx}`} variant="outlined" sx={{ p: 1 }}>
                      <Grid container spacing={1}>
                        {Object.entries(item).map(([nestedKey, nestedVal]) =>
                          renderEditableField(planIndex, nestedKey, nestedVal, [
                            ...keyPath,
                            idx,
                          ])
                        )}
                      </Grid>
                    </Paper>
                  );
                }
                return (
                  <TextField
                    key={`${keyPath.join(".")}.${idx}`}
                    size="small"
                    fullWidth
                    label={`${fieldLabel} ${idx + 1}`}
                    value={item ?? ""}
                    onChange={(e) =>
                      handleEditPlanDataByPath(planIndex, [...keyPath, idx], e.target.value)
                    }
                  />
                );
              })}
            </Box>
          </Box>
        </Grid>
      );
    }

    if (fieldValue && typeof fieldValue === "object") {
      return (
        <Grid item xs={12} key={keyPath.join(".")}>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {fieldLabel}
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
              <Grid container spacing={1}>
                {Object.entries(fieldValue).map(([nestedKey, nestedVal]) =>
                  renderEditableField(planIndex, nestedKey, nestedVal, keyPath)
                )}
              </Grid>
            </Paper>
          </Box>
        </Grid>
      );
    }

    if (typeof fieldValue === "boolean") {
      return (
        <Grid item xs={12} sm={6} key={keyPath.join(".")}>
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={
              <Checkbox
                checked={Boolean(fieldValue)}
                onChange={(e) =>
                  handleEditPlanDataByPath(planIndex, keyPath, e.target.checked)
                }
              />
            }
            label={fieldLabel}
          />
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={6} key={keyPath.join(".")}>
        <TextField
          size="small"
          fullWidth
          label={fieldLabel}
          value={fieldValue ?? ""}
          onChange={(e) => handleEditPlanDataByPath(planIndex, keyPath, e.target.value)}
          sx={{ mt: 0.5 }}
        />
      </Grid>
    );
  };

  const handleSaveReviewedPlans = async () => {
    if (!onSubmit) return;
    setIsSubmitting(true);
    try {
      const editedPlans = reviewPlans.map((plan) => {
        return {
          type: plan.type,
          name: plan.name,
          care_plan_id: plan.care_plan_id,
          data: plan.data || {},
        };
      });

      const success = await onSubmit({
        mode: "persist",
        carePlans: editedPlans,
      });

      if (success !== false) {
        handleClose();
      }
    } catch (error) {
      setLimitMessage(error?.message || "Please fix invalid review data.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSectionTitle = (plan) => {
    const normalized = inferCarePlanType(plan);
    const titleMap = {
      medication: "Medication",
      test: "Test",
      "clinical-notes": "Note",
      monitoring: "Monitoring",
      diet: "Nutrition",
      appointment: "Appointment",
      "task-list": "Task",
    };
    return titleMap[normalized] || toLabel(normalized || "Activity");
  };

  // Group review plans by their resolved care_plan_structure (falling back to the
  // inferred type). Each group renders one accordion; each plan an inner entry.
  const reviewGroups = useMemo(() => {
    const groups = new Map();
    reviewPlans.forEach((plan, planIndex) => {
      const structure = findStructureForPlan(plan);
      const key = structure
        ? `structure-${getStructureId(structure) || normalizeKey(structure.name)}`
        : `type-${inferCarePlanType(plan) || "activity"}`;
      const title = structure?.name || getSectionTitle(plan);
      if (!groups.has(key)) {
        groups.set(key, { key, title, structure, entries: [] });
      }
      groups.get(key).entries.push({ plan, planIndex });
    });
    return Array.from(groups.values());
  }, [reviewPlans, findStructureForPlan]);

  // Renders a single plan entry's editable fields (structure-driven, or the
  // generic fallback when no structure matched).
  const renderPlanFields = (section, plan, planIndex, hideEmpty) => (
    <Grid container spacing={1.5}>
      {section.structure
        ? (section.structure.structure || []).map((field) =>
            renderStructureField(planIndex, field, plan.data || {}, [], hideEmpty)
          )
        : Object.entries(plan.data || {}).map(([fieldKey, fieldValue]) =>
            renderEditableField(planIndex, fieldKey, fieldValue, [])
          )}
      {hideEmpty &&
        section.structure &&
        !(section.structure.structure || []).some((field) =>
          fieldHasData(field, getNestedValue(plan.data || {}, [field.field]))
        ) && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No notes captured from the recording.
            </Typography>
          </Grid>
        )}
    </Grid>
  );

  const handleClose = () => {
    handleStopRecording();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={step === "review" ? "lg" : "sm"}
      PaperProps={{
        sx:
          step === "review"
            ? {
              width: "92vw",
              maxWidth: "1200px",
              height: "92vh",
            }
            : {
              width: "100%",
              maxWidth: "640px",
            },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "1.2rem",
          color: "#125386",
        }}
      >
        Voice Assistant
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "12px 24px" }}>
        {step === "record" ? (
          <>
            <Box
              sx={{
                backgroundColor: "#FFF8E1",
                border: "1px solid #FFECB3",
                borderRadius: "6px",
                padding: "0px 12px",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                color: "#795548",
                fontSize: "0.875rem",
                lineHeight: 1.4,
                marginBottom: 1.5,
              }}
            >
              <span style={{ fontSize: "18px", flexShrink: 0 }}>⚠️</span>
              <Typography
                component="span"
                sx={{
                  display: "inline",
                  flex: 1,
                  wordBreak: "break-word",
                  fontSize: "0.8rem",
                }}
              >
                AI-generated content may contain transcription or interpretation
                errors. Please review and correct all details before submitting.
              </Typography>
            </Box>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isRecording}
                displayEmpty
                sx={{ height: "60%" }}
                MenuProps={{
                  disablePortal: true,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  PaperProps: { style: { maxHeight: 200, width: "auto" } },
                }}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Grid container justifyContent="space-between">
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={8}
                  display="flex"
                  justifyContent="flex-start"
                  gap={1}
                >
                  <Grid item xs={6} md={6} lg={5}>
                    <StyledButton
                      bgColor="#0E3F66"
                      onClick={handleStartRecording}
                      disabled={isRecording}
                      variant="contained"
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.8rem" },
                        width: "90%",
                        height: "90%",
                        "&:hover": { backgroundColor: "#07367dff" },
                      }}
                    >
                      Start Recording
                    </StyledButton>
                  </Grid>
                  <Grid item xs={6} md={6} lg={4}>
                    <StyledButton
                      bgColor={isPaused ? "#5DAA60" : "#FFA500"}
                      onClick={isPaused ? handleResumeRecording : handlePauseRecording}
                      disabled={!isRecording}
                      variant="contained"
                      fullWidth
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.8rem" },
                        width: "90%",
                        height: "90%",
                        "&:hover": {
                          backgroundColor: isPaused ? "#1b5e20" : "#FF8C00",
                        },
                      }}
                    >
                      {isPaused ? "Resume" : "Pause"}
                    </StyledButton>
                  </Grid>

                  <Grid item xs={6} md={6} lg={5}>
                    <StyledButton
                      bgColor="#FF3B30"
                      onClick={handleStopRecording}
                      disabled={!isRecording}
                      variant="contained"
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.8rem" },
                        width: "90%",
                        height: "90%",
                        "&:hover": { backgroundColor: "#b71c1c" },
                      }}
                    >
                      Stop Recording
                    </StyledButton>
                  </Grid>
                </Grid>

                <Grid item xs={6} md={6} lg={3}>
                  <StyledButton
                    bgColor="#5DAA60"
                    onClick={handleSubmit}
                    disabled={isRecording || !audioBlob || isSubmitting}
                    variant="contained"
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.8rem" },
                      width: "90%",
                      height: "90%",
                      "&:hover": { backgroundColor: "#1b5e20" },
                    }}
                  >
                    {isSubmitting ? "Processing..." : "Submit"}
                  </StyledButton>
                </Grid>
              </Grid>
            </Box>

            {isRecording && streamRef.current && (
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <VoiceWaveform
                      stream={streamRef.current}
                      isRecording={isRecording}
                      isPaused={isPaused}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: 110,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CountdownCircleTimer
                      key={timerKey}
                      isPlaying={isRecording && !isPaused}
                      duration={240}
                      size={90}
                      strokeWidth={8}
                      colors={["#5DAA60", "#55C1A1", "#00A3E0", "#0E3F66"]}
                      colorsTime={[240, 160, 80, 0]}
                      onComplete={() => {
                        return { shouldRepeat: false, delay: 0 };
                      }}
                    >
                      {({ remainingTime }) => (
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.9rem", fontWeight: 700 }}
                        >
                          {formatMMSS(remainingTime)}
                        </Typography>
                      )}
                    </CountdownCircleTimer>
                  </Box>
                </Box>
              </Box>
            )}

            {audioBlob && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Preview:
                </Typography>
                <audio controls src={URL.createObjectURL(audioBlob)} />
              </Box>
            )}
          </>
        ) : (
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              Review AI Generated Prescriptions
            </Typography>
            <Box sx={{ mb: 2 }}>
              {reviewGroups.map((section) => {
                // Clinical Notes: show only the fields the doctor actually dictated
                // (hide the empty template fields/tables), like tests/medication.
                const hideEmpty =
                  normalizeKey(section.structure?.name) === "clinicalnotes";
                // Tests list every order in one table, so skip the per-entry
                // accordion header and render straight from the Orders table.
                const flatEntries =
                  normalizeKey(section.structure?.name) === "test";
                return (
                  <Accordion key={section.key} defaultExpanded disableGutters sx={{ mb: 1.25, border: "1px solid #e0e0e0", borderRadius: 1, "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0.5 }}>
                      {!flatEntries && section.structure && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mb: 1,
                          }}
                        >
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addBlankEntryForSection(section)}
                            sx={{
                              fontSize: "0.73rem",
                              textTransform: "none",
                              color: "#21cdc0",
                            }}
                          >
                            Add {section.title}
                          </Button>
                        </Box>
                      )}
                      {flatEntries
                        ? section.entries.map(({ plan, planIndex }) => (
                            <Box key={`entry-${section.key}-${planIndex}`} sx={{ mb: 1 }}>
                              {section.entries.length > 1 && (
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => deleteReviewPlan(planIndex)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                              {renderPlanFields(section, plan, planIndex, hideEmpty)}
                            </Box>
                          ))
                        : section.entries.map(({ plan, planIndex }, entryIndex) => (
                            <Accordion
                              key={`entry-${section.key}-${planIndex}`}
                              defaultExpanded={section.entries.length === 1}
                              disableGutters
                              sx={{
                                mb: 1,
                                border: "1px solid #ececec",
                                borderRadius: 1,
                                "&:before": { display: "none" },
                              }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box
                                  sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    pr: 1,
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {getPlanEntryLabel(plan, section.structure, entryIndex)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteReviewPlan(planIndex);
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                {renderPlanFields(section, plan, planIndex, hideEmpty)}
                              </AccordionDetails>
                            </Accordion>
                          ))}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setStep("record")}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveReviewedPlans}
                disabled={isSubmitting || reviewPlans.length === 0}
              >
                {isSubmitting ? "Saving..." : "Save Reviewed Plans"}
              </Button>
            </Box>
          </Box>
        )}

        {limitMessage && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 1, textAlign: "center" }}
          >
            {limitMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions />
    </Dialog>
  );
};

export default VoiceAssistantModal;

